import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  ShieldAlert,
  ArrowLeft,
  Activity,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Webhook,
  Receipt,
  PlayCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/admin/onvo-health")({
  head: () => ({ meta: [{ title: "Admin · Salud ONVO Pay — ZYNTRA" }] }),
  component: AdminOnvoHealthPage,
});

interface OnvoEvent {
  event_id: string;
  event_type: string | null;
  processed_at: string;
  session_or_intent_id: string | null;
}

interface OnvoSession {
  session_id: string;
  ticket_count: number;
  tickets_gross: number;
  settlement_count: number;
  settlement_gross: number;
  settlement_status: string | null;
  webhook_event_count: number;
  event_types: string | null;
  last_event_at: string | null;
  event_id: string | null;
  user_id: string | null;
  last_seen_at: string;
  missing_tickets: boolean;
  missing_settlement: boolean;
}

interface OnvoAlerts {
  webhook_stale: boolean;
  last_webhook_at: string | null;
  webhook_age_seconds: number | null;
  wallet_drift_count: number;
  inflight_zombies: number;
  sessions_missing_tickets: number;
  sessions_missing_settlement: number;
}

interface HealthPayload {
  snapshot_at: string;
  lookback_minutes: number;
  recent_events: OnvoEvent[];
  sessions: OnvoSession[];
  alerts: OnvoAlerts;
}

const LOOKBACK_OPTIONS = [
  { label: "1h", value: 60 },
  { label: "6h", value: 360 },
  { label: "24h", value: 1440 },
  { label: "7d", value: 10080 },
];

function AdminOnvoHealthPage() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [data, setData] = useState<HealthPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lookback, setLookback] = useState(60);
  const [staleSeconds, setStaleSeconds] = useState(1800);
  const [replayId, setReplayId] = useState("");
  const [replayBusy, setReplayBusy] = useState(false);
  const [replayResult, setReplayResult] = useState<string | null>(null);
  const refreshRef = useRef<(() => Promise<void>) | null>(null);

  async function refresh() {
    setRefreshing(true);
    const { data: rpc, error } = await supabase.rpc("get_onvo_health", {
      _lookback_minutes: lookback,
      _webhook_stale_seconds: staleSeconds,
    });
    if (!error && rpc) setData(rpc as unknown as HealthPayload);
    setRefreshing(false);
  }
  refreshRef.current = refresh;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!roleRow);
      if (roleRow) await refresh();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  useEffect(() => {
    if (!isAdmin) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookback, staleSeconds]);

  // Auto-refresh cada 30s
  useEffect(() => {
    if (!isAdmin) return;
    const id = setInterval(() => refreshRef.current?.(), 30000);
    return () => clearInterval(id);
  }, [isAdmin]);

  async function handleReplay() {
    const sid = replayId.trim();
    if (!sid) return;
    setReplayBusy(true);
    setReplayResult(null);
    try {
      const { data: res, error } = await supabase.functions.invoke("onvopay-replay", {
        body: { session_id: sid },
      });
      if (error) {
        setReplayResult(`❌ ${error.message ?? "Error desconocido"}`);
      } else if (res?.error) {
        setReplayResult(`❌ ${res.error}${res.hint ? ` — ${res.hint}` : ""}`);
      } else if (res?.no_op) {
        setReplayResult(
          `ℹ️ Idempotente: nada que crear. Tickets=${res.tickets_after}, Settlements=${res.settlements_after}`,
        );
      } else {
        setReplayResult(
          `✅ Fulfilment OK. Tickets ${res.tickets_before}→${res.tickets_after}, Settlements ${res.settlements_before}→${res.settlements_after}`,
        );
      }
      await refresh();
    } catch (e: any) {
      setReplayResult(`❌ ${e?.message ?? "fallo de red"}`);
    } finally {
      setReplayBusy(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user || !isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  const a = data?.alerts;
  const anyAlert =
    !!a &&
    (a.webhook_stale ||
      a.wallet_drift_count > 0 ||
      a.inflight_zombies > 0 ||
      a.sessions_missing_tickets > 0 ||
      a.sessions_missing_settlement > 0);

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6">
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/admin/health"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Salud operacional
        </Link>
        <div className="flex items-center gap-2">
          <select
            value={lookback}
            onChange={(e) => setLookback(Number(e.target.value))}
            className="rounded-lg border border-border bg-secondary px-2 py-1 text-xs"
          >
            {LOOKBACK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Ventana: {o.label}
              </option>
            ))}
          </select>
          <select
            value={staleSeconds}
            onChange={(e) => setStaleSeconds(Number(e.target.value))}
            className="rounded-lg border border-border bg-secondary px-2 py-1 text-xs"
          >
            <option value={300}>Stale: 5m</option>
            <option value={900}>Stale: 15m</option>
            <option value={1800}>Stale: 30m</option>
            <option value={3600}>Stale: 1h</option>
          </select>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Activity className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Salud de pagos ONVO</h1>
        {data?.snapshot_at && (
          <span className="ml-2 text-xs text-muted-foreground">
            {new Date(data.snapshot_at).toLocaleString("es-CR")}
          </span>
        )}
      </div>

      {/* ALERTAS */}
      <SectionHeader title="Alertas" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <AlertCard
          label="Último webhook"
          tone={a?.webhook_stale ? "danger" : "ok"}
          value={
            a?.last_webhook_at
              ? `${new Date(a.last_webhook_at).toLocaleTimeString("es-CR")} · ${a.webhook_age_seconds ?? 0}s atrás`
              : "Nunca"
          }
          hint={a?.webhook_stale ? "Webhook stale: posible problema de red ONVO" : "OK"}
        />
        <AlertCard
          label="Wallet drift"
          tone={a && a.wallet_drift_count > 0 ? "danger" : "ok"}
          value={`${a?.wallet_drift_count ?? 0} wallets`}
          hint={a && a.wallet_drift_count > 0 ? "Pausar operación inmediato" : "Drift = 0"}
        />
        <AlertCard
          label="Inflight zombies"
          tone={a && a.inflight_zombies > 0 ? "warn" : "ok"}
          value={`${a?.inflight_zombies ?? 0}`}
          hint=">10min sin terminar"
        />
        <AlertCard
          label="Sesiones sin tickets"
          tone={a && a.sessions_missing_tickets > 0 ? "danger" : "ok"}
          value={`${a?.sessions_missing_tickets ?? 0}`}
          hint="Webhook llegó pero fulfilment no creó tickets"
        />
        <AlertCard
          label="Sesiones sin settlement"
          tone={a && a.sessions_missing_settlement > 0 ? "warn" : "ok"}
          value={`${a?.sessions_missing_settlement ?? 0}`}
          hint="Tickets emitidos sin liquidación al organizador"
        />
        <AlertCard
          label="Estado global"
          tone={anyAlert ? "danger" : "ok"}
          value={anyAlert ? "REVISAR" : "OK"}
        />
      </div>

      {/* REPLAY MANUAL */}
      <SectionHeader title="Reintento manual de fulfilment" />
      <div className="rounded-xl border border-border bg-card p-4">
        <p className="mb-3 text-xs text-muted-foreground">
          Reintenta el fulfilment para una <code>checkout_session_id</code> de ONVO. Reusa el mismo
          handler del webhook (idempotente: no duplica tickets ni settlements). La acción queda
          registrada en <code>admin_actions_log</code>.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={replayId}
            onChange={(e) => setReplayId(e.target.value)}
            placeholder="ej. test_YQIKoIxb63pN2HiZfXtfUX0ST94"
            className="flex-1 rounded-lg border border-border bg-secondary px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground"
          />
          <button
            onClick={handleReplay}
            disabled={replayBusy || !replayId.trim()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {replayBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            Reintentar fulfilment
          </button>
        </div>
        {replayResult && (
          <p className="mt-3 rounded-lg bg-secondary px-3 py-2 text-xs">{replayResult}</p>
        )}
      </div>

      {/* WEBHOOKS RECIENTES */}
      <SectionHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Webhook className="h-4 w-4" /> Últimos webhooks ({data?.recent_events.length ?? 0})
          </span>
        }
      />
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Hora</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Session/Intent ID</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recent_events ?? []).map((e) => (
              <tr key={e.event_id} className="border-t border-border">
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  {new Date(e.processed_at).toLocaleTimeString("es-CR")}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${
                      e.event_type === "checkout-session.succeeded"
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        : e.event_type?.endsWith("failed")
                          ? "bg-red-500/10 text-red-600 dark:text-red-400"
                          : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {e.event_type ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                  {e.session_or_intent_id ?? "—"}
                </td>
              </tr>
            ))}
            {(!data?.recent_events || data.recent_events.length === 0) && (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Sin eventos en la ventana seleccionada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* SESIONES */}
      <SectionHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Sesiones ONVO ({data?.sessions.length ?? 0})
          </span>
        }
      />
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Session ID</th>
              <th className="px-3 py-2 text-right">Tickets</th>
              <th className="px-3 py-2 text-right">Bruto</th>
              <th className="px-3 py-2 text-right">Settlement</th>
              <th className="px-3 py-2 text-right">Eventos</th>
              <th className="px-3 py-2 text-left">Última actividad</th>
              <th className="px-3 py-2 text-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            {(data?.sessions ?? []).map((s) => (
              <tr key={s.session_id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-1.5">
                    {(s.missing_tickets || s.missing_settlement) && (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    )}
                    <span className="truncate max-w-[200px]" title={s.session_id}>
                      {s.session_id}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={s.missing_tickets ? "text-red-500 font-semibold" : ""}>
                    {s.ticket_count}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  ₡{Math.round(Number(s.tickets_gross)).toLocaleString("es-CR")}
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  {s.settlement_count > 0 ? (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      ₡{Math.round(Number(s.settlement_gross)).toLocaleString("es-CR")} ·{" "}
                      {s.settlement_status}
                    </span>
                  ) : s.ticket_count > 0 ? (
                    <span className="text-red-500">FALTA</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-xs" title={s.event_types ?? ""}>
                  {s.webhook_event_count}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {new Date(s.last_seen_at).toLocaleTimeString("es-CR")}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => {
                      setReplayId(s.session_id);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="rounded border border-border bg-secondary px-2 py-1 text-xs hover:bg-accent"
                    title="Cargar en el formulario de replay"
                  >
                    Replay
                  </button>
                </td>
              </tr>
            ))}
            {(!data?.sessions || data.sessions.length === 0) && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Sin sesiones en la ventana seleccionada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: React.ReactNode }) {
  return <h2 className="mb-3 mt-8 text-sm font-semibold text-foreground">{title}</h2>;
}

function AlertCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "danger";
  hint?: string;
}) {
  const toneStyles =
    tone === "danger"
      ? "border-red-500/40 bg-red-500/5"
      : tone === "warn"
        ? "border-amber-500/40 bg-amber-500/5"
        : "border-border bg-card";
  const Icon = tone === "ok" ? CheckCircle2 : AlertTriangle;
  const iconColor =
    tone === "danger" ? "text-red-500" : tone === "warn" ? "text-amber-500" : "text-emerald-500";
  return (
    <div className={`rounded-xl border p-3 ${toneStyles}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
